import { Typography } from '@material-ui/core';
import { Info, ThumbDown, ThumbUp } from '@material-ui/icons';
import { FormikMaxLengthTextField } from '@superdispatch/forms';
import { ColorDynamic, Column, Columns, Stack } from '@superdispatch/ui';
import { Box, ButtonArea, TextBox } from '@superdispatch/ui-lab';
import { useFormikContext } from 'formik';
import { useAppFormik } from 'shared/form/AppFormik';
import {
  FormikCheckboxArrayField,
  FormikCheckboxArrayGroup,
} from 'shared/form/FormikCheckboxArrayGroup';
import { trackRatingSubmittedAnalytics } from 'shared/modules/ratings/data/RatingAnalytics';
import {
  useLatestShipperRating,
  useShipperRatingsAPI,
} from 'shared/modules/ratings/data/ShipperRatingsAPI';
import { WebViewLink } from 'shared/routing/WebViewLinks';
import {
  getShipperRatingTagDisplayName,
  NEGATIVE_SHIPPER_RATING_TAGS,
  POSITIVE_SHIPPER_RATING_TAGS,
  RatingType,
  ShipperNewRatingDTO,
  shipperNewRatingSchema,
  ShipperRatingDTO,
} from './data/ShipperRatingDTO';
import { ReactNode, useMemo } from 'react';

interface RatingShipperFormProps {
  hideRatingSelection?: boolean;
  additionalDetails?: ReactNode;
}

export function RatingShipperForm({
  hideRatingSelection,

  additionalDetails,
}: RatingShipperFormProps) {
  const { values, errors, touched, initialValues, setValues } =
    useFormikContext<ShipperNewRatingDTO>();

  const hasTagsError = touched.tags && errors.tags;
  const hasEditedNegativeRating =
    initialValues.rating !== '' && values.rating === 'NEGATIVE';

  return (
    <Stack space="medium">
      {!hideRatingSelection && (
        <Stack space="small">
          <Typography variant="h4">Rate your recent experience</Typography>

          <Columns space="small">
            <Column>
              <ButtonArea
                fullWidth={true}
                variant="danger"
                icon={<ThumbDown />}
                active={values.rating === 'NEGATIVE'}
                onClick={() => {
                  void setValues({ ...values, rating: 'NEGATIVE', tags: [] });
                }}
              >
                Negative
              </ButtonArea>
            </Column>

            <Column>
              <ButtonArea
                fullWidth={true}
                icon={<ThumbUp />}
                variant="success"
                active={values.rating === 'POSITIVE'}
                onClick={() => {
                  void setValues({ ...values, rating: 'POSITIVE', tags: [] });
                }}
              >
                Positive
              </ButtonArea>
            </Column>
          </Columns>
        </Stack>
      )}

      {(hasEditedNegativeRating || additionalDetails) && (
        <Box padding="small" borderRadius="small" backgroundColor="Silver200">
          <Stack>
            {additionalDetails}
            {hasEditedNegativeRating && (
              <Columns space="xsmall">
                <Column width="content">
                  <Box top="2px" position="relative">
                    <Info fontSize="small" htmlColor={ColorDynamic.Dark100} />
                  </Box>
                </Column>
                <Column>
                  <TextBox color="secondary">
                    Negative ratings stay pending for 10 days and can be deleted
                    and edited during that time, even if the 90 days have
                    already passed.
                  </TextBox>
                </Column>
              </Columns>
            )}
          </Stack>
        </Box>
      )}

      {values.rating ? (
        <>
          <Stack space="small">
            {values.rating === 'POSITIVE' ? (
              <Typography variant="h4">What went well?</Typography>
            ) : (
              <div>
                <Typography variant="h4">What went wrong?</Typography>
                <Typography
                  variant="caption"
                  color={hasTagsError ? 'error' : 'textSecondary'}
                >
                  {hasTagsError ? 'Select reason below' : 'Required'}
                </Typography>
              </div>
            )}

            {values.rating === 'POSITIVE' ? (
              <FormikCheckboxArrayGroup name="tags">
                <Stack space="none">
                  {POSITIVE_SHIPPER_RATING_TAGS.map((tag) => (
                    <FormikCheckboxArrayField
                      key={tag}
                      name={tag}
                      label={getShipperRatingTagDisplayName(tag)}
                    />
                  ))}
                </Stack>
              </FormikCheckboxArrayGroup>
            ) : (
              <FormikCheckboxArrayGroup name="tags">
                <Stack space="none">
                  {NEGATIVE_SHIPPER_RATING_TAGS.map((tag) => (
                    <FormikCheckboxArrayField
                      key={tag}
                      name={tag}
                      label={getShipperRatingTagDisplayName(tag)}
                    />
                  ))}
                </Stack>
              </FormikCheckboxArrayGroup>
            )}
          </Stack>

          <FormikMaxLengthTextField
            name="comment"
            label="Tell more about it"
            placeholder={
              values.rating === 'POSITIVE'
                ? 'Very communicative, paid on time...'
                : 'What disappointed you the most? \nWhat happened?'
            }
            helperText="Publicly visible."
            minRows={3}
            maxLength={280}
            multiline={true}
            fullWidth={true}
          />
        </>
      ) : null}

      <WebViewLink
        href="https://support.superdispatch.com/hc/articles/35661788289555-Ratings-in-Super-Dispatch"
        target="_blank"
        rel="noreferrer"
      >
        Learn more about ratings
      </WebViewLink>
    </Stack>
  );
}

interface RatingsShipperFormOptions {
  guid: string;
  ratingType?: RatingType;
  medium?: string;
  onSuccess: () => void;
}

export function useRatingShipperForm({
  guid,
  ratingType,
  medium,
  onSuccess,
}: RatingsShipperFormOptions) {
  const { rateShipper } = useShipperRatingsAPI();
  const { data: latestShipperRating } = useLatestShipperRating(guid);

  const isEditingRating =
    !!latestShipperRating?.guid && ratingType === undefined;
  const initialValues = useMemo(() => {
    if (isEditingRating && latestShipperRating) {
      return latestShipperRating;
    }
    return { rating: ratingType ?? '' };
  }, [latestShipperRating, ratingType, isEditingRating]);

  const formik = useAppFormik<ShipperNewRatingDTO, ShipperRatingDTO>({
    validationSchema: shipperNewRatingSchema,
    initialValues,
    onSubmit: (values) => rateShipper(guid, values),
    onSubmitSuccess: (values) => {
      trackRatingSubmittedAnalytics(
        'Carrier Submitted Rating',
        values.data,
        medium,
      );
      onSuccess();
    },
    getSuccessMessage: () =>
      isEditingRating ? 'Rating updated' : 'Rating submitted',
    getErrorMessage: ({ message }) => {
      if (message === 'Forbidden') {
        return 'No rating permission. Contact manager to resolve the issue.';
      }

      return message;
    },
  });

  return { formik, isEditingRating };
}
